// web/src/data/user.ts
import { auth, db, storage } from "@/lib/firebase";
import {
  doc,
  serverTimestamp,
  updateDoc,
  setDoc,
  getDoc,
  collection,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

/* =======================================================
   Types
======================================================= */

export type PhotoSource = "upload" | "preset";

export type AvailabilityPatch = {
  /** If provided, toggles marketplace visibility + legacy mirror field */
  trade?: boolean;
  /** If provided, toggles community visibility + legacy mirror field */
  play?: boolean;
};

export type DevicePlatform = "web" | "ios" | "android";

export type EmailPrefs = {
  /** Offers for trade or purchase addressed to me */
  offers: boolean;
  /** New followers */
  follows: boolean;
  /** Direct messages */
  direct_messages: boolean;
  /** Optional: routine product or platform updates (off by default) */
  announcements?: boolean;
};

export type LegalAck = {
  /** Version string of your legal bundle (e.g. "2025-08-22") */
  version: string;
  /** User asserted they are at/over required age */
  age_confirmed: boolean;
};

/* =======================================================
   Ensure base user document exists (non-destructive)
======================================================= */

export async function ensureUserDoc(): Promise<void> {
  const u = auth.currentUser;
  if (!u) throw new Error("Not signed in.");

  const dref = doc(db, "users", u.uid);
  const snap = await getDoc(dref);

  if (!snap.exists()) {
    await setDoc(
      dref,
      {
        // Timestamps
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),

        // Minimal identity mirrors (non-authoritative; UI may replace later)
        email: u.email ?? null,
        display_name: u.displayName ?? null,
        photoURL: u.photoURL ?? null,

        // Preferences / presence defaults
        preferences: {
          available_for_trading: true,
          available_for_play: true,
          collection_browsing: true,
          wishlist_is_public: false,
        },
        presence: {
          show_in_marketplace: true,
          show_in_community_stream: true,
        },
        visibility: {
          display_name: true,
          bio: true,
          location: true,
          play_formats: true,
          meetup_types: true,
          competitive_preference: true,
          commander_tiers: true,
          play_radius: false,
          trade_radius: false,
          available_for_trading: true,
          available_for_play: true,
          collection_browsing: true,
          wishlist_is_public: false,
          preferred_lgs: true,
        },
      },
      { merge: true },
    );
  }
}

/* =======================================================
   Avatar helpers (canonical field: avatar_url)
   - Also writes legacy photoURL + photoUpdatedAt for back-compat.
======================================================= */

export async function setUserAvatarURL(
  url: string,
  source: PhotoSource,
  hint?: string,
): Promise<void> {
  const u = auth.currentUser;
  if (!u) throw new Error("Not signed in.");
  await ensureUserDoc();
  const dref = doc(db, "users", u.uid);

  await updateDoc(dref, {
    avatar_url: url, // new primary field
    // legacy mirrors (kept in sync for old UIs)
    photoURL: url,
    photoSource: source,
    photoHint: hint ?? null,
    photoUpdatedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

/** Upload a file to Storage and save its URL into users/{uid}. */
export async function setUserAvatarFromFile(file: File): Promise<string> {
  const u = auth.currentUser;
  if (!u) throw new Error("Not signed in.");

  // Static Storage upload

  const ext = (file.name.split(".").pop() || "png").toLowerCase();
  const path = `avatars/${u.uid}/avatar.${ext}`;
  const rf = ref(storage, path);
  await uploadBytes(rf, file, { contentType: file.type || "image/*", cacheControl: "public, max-age=3600" });
  const url = await getDownloadURL(rf);
  await setUserAvatarURL(url, "upload", file.name);
  return url;
}

/** Use a preset (CDN/public) URL and save into users/{uid}. */
export async function setUserAvatarFromPreset(
  presetUrl: string,
  presetKey?: string,
): Promise<void> {
  await setUserAvatarURL(presetUrl, "preset", presetKey);
}

/* =======================================================
   Availability / Presence
   - Writes both nested canonical fields and shallow mirrors
     (availableForTrade / availableForPlay) for compatibility.
======================================================= */

export async function setAvailability(patch: AvailabilityPatch): Promise<void> {
  const u = auth.currentUser;
  if (!u) throw new Error("Not signed in.");
  await ensureUserDoc();

  const updates: Record<string, unknown> = {
    updatedAt: serverTimestamp(),
  };

  if (typeof patch.trade === "boolean") {
    updates["preferences.available_for_trading"] = patch.trade;
    updates["presence.show_in_marketplace"] = patch.trade;
    updates["availableForTrade"] = patch.trade; // legacy/mirror
  }
  if (typeof patch.play === "boolean") {
    updates["preferences.available_for_play"] = patch.play;
    updates["presence.show_in_community_stream"] = patch.play;
    updates["availableForPlay"] = patch.play; // legacy/mirror
  }

  await updateDoc(doc(db, "users", u.uid), updates);
}

/* =======================================================
   Contact email & notification preferences
   - Does NOT touch Firebase Auth email/password.
======================================================= */

export async function setContactEmail(email: string): Promise<void> {
  const u = auth.currentUser;
  if (!u) throw new Error("Not signed in.");
  await ensureUserDoc();

  await updateDoc(doc(db, "users", u.uid), {
    contact_email: email || null,
    contact_email_updatedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function setEmailPreferences(prefs: EmailPrefs): Promise<void> {
  const u = auth.currentUser;
  if (!u) throw new Error("Not signed in.");
  await ensureUserDoc();

  const normalized: EmailPrefs = {
    offers: Boolean(prefs.offers),
    follows: Boolean(prefs.follows),
    direct_messages: Boolean(prefs.direct_messages),
    announcements: Boolean(prefs.announcements ?? false),
  };

  await updateDoc(doc(db, "users", u.uid), {
    email_prefs: normalized,
    email_prefs_updatedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

/* =======================================================
   Push device registration (used by NotificationsBell)
   - Stores tokens under users/{uid}/devices/{token}
======================================================= */

export async function saveFcmToken(
  token: string,
  platform: DevicePlatform,
): Promise<void> {
  const u = auth.currentUser;
  if (!u) throw new Error("Not signed in.");
  if (!token.trim()) throw new Error("Missing token.");
  await ensureUserDoc();

  const dref = doc(collection(db, "users", u.uid, "devices"), token);
  await setDoc(
    dref,
    {
      token,
      platform,
      lastSeenAt: serverTimestamp(),
      createdAt: serverTimestamp(),
    },
    { merge: true },
  );
}

/* =======================================================
   Legal acknowledgements (Terms / Privacy / Community / Data)
   - Writes both snake_case and camelCase mirrors for back-compat:
     legal.age_confirmed + legal.ageConfirmed
     legal.acceptedAt + legal.accepted
======================================================= */

export async function acknowledgeLegal(ack: LegalAck): Promise<void> {
  const u = auth.currentUser;
  if (!u) throw new Error("Not signed in.");
  if (!ack.version) throw new Error("Missing legal version.");
  await ensureUserDoc();

  const acceptedAt = serverTimestamp();

  await updateDoc(doc(db, "users", u.uid), {
    legal: {
      version: ack.version,
      // mirrors for newer/older code paths
      age_confirmed: Boolean(ack.age_confirmed),
      ageConfirmed: Boolean(ack.age_confirmed),
      acceptedAt, // Timestamp used by most recent gates
      accepted: true, // legacy boolean used by older gates
    },
    updatedAt: acceptedAt,
  });
}

/* =======================================================
   Convenience: merge multiple preference changes at once
======================================================= */

export type PreferencesPatch = {
  collection_browsing?: boolean;
  wishlist_is_public?: boolean;
};

export async function updatePreferences(p: PreferencesPatch): Promise<void> {
  const u = auth.currentUser;
  if (!u) throw new Error("Not signed in.");
  await ensureUserDoc();

  const updates: Record<string, unknown> = { updatedAt: serverTimestamp() };
  if (typeof p.collection_browsing === "boolean") {
    updates["preferences.collection_browsing"] = p.collection_browsing;
  }
  if (typeof p.wishlist_is_public === "boolean") {
    updates["preferences.wishlist_is_public"] = p.wishlist_is_public;
  }
  await updateDoc(doc(db, "users", u.uid), updates);
}

